// Wire shapes of the admin console API (/api/admin/*), shared by the API
// (apps/api/src/routes/admin.ts) and the console (apps/web/src/admin).
//
// Raw facts, not presentation (ADR-0009): ISO-8601 timestamps, counts and
// daily series. The console formats, buckets and compares. Fields backed by
// Analytics Engine are null when it isn't queryable in that environment
// (`analytics: "unavailable"`).

import type { AdminSupportSession } from "./support";

export type AdminAnalytics = "available" | "unavailable";
export type AdminEnvironment = "prod" | "dev" | "local";
export const ADMIN_RANGES = [7, 30, 90] as const;
export type AdminRangeDays = (typeof ADMIN_RANGES)[number];

export interface AdminMe {
  admin: { userId: string; email: string; name: string };
  environment: AdminEnvironment;
  host: string;
  analytics: AdminAnalytics;
}

export interface AdminRange {
  days: number;
  /** First UTC day of the current window (inclusive). */
  from: string;
  /** Today, UTC — the window ends the day before (exclusive). */
  to: string;
}

export interface AdminOverview {
  range: AdminRange;
  analytics: AdminAnalytics;
  /** 2 × days UTC days: the previous window, then the current one. Every series below lines up with it. */
  days: string[];
  series: {
    signups: number[];
    /** Of the runners who signed up that day, how many are verified now. */
    verified: number[];
    plansStarted: number[];
    exports: number[] | null;
    mcpCalls: number[] | null;
    activeDaily: number[] | null;
    /** Distinct runners active in the 7 days up to each day. */
    activeWeekly: number[] | null;
  };
  /** The last 8 days (yesterday last), for the health strip's "yesterday vs 7-day average". */
  health: { days: string[]; errors5xx: number[]; authFailures: number[]; rateLimited: number[] } | null;
  /** Runners who signed up in the current window. */
  funnel: {
    signedUp: number;
    verified: number;
    startedPlan: number;
    /** Of those, signed up 14+ days ago — the only ones week-2 activity can be judged for. */
    eligibleWeek2: number;
    activeWeek2: number | null;
  };
  exportsByFormat: Array<{ format: string; n: number }> | null;
  mcp: {
    calls: number;
    errors: number;
    rateLimited: number;
    runners: number;
    tools: Array<{ tool: string; calls: number; errors: number }>;
  } | null;
}

export const RUNNER_FILTERS = [
  "verified",
  "unverified",
  "active_plan",
  "assistant",
  "signed_30d",
  "seen_7d",
  "support_access",
  "banned",
] as const;
export type RunnerFilter = (typeof RUNNER_FILTERS)[number];

export const RUNNER_SORTS = ["name", "signed", "seen", "plans", "exports", "assistants"] as const;
export type RunnerSort = (typeof RUNNER_SORTS)[number];

export interface AdminRunnerRow {
  id: string;
  name: string;
  email: string;
  emailVerified: boolean;
  createdAt: string;
  lastSeenAt: string | null;
  /** Plans not archived (the running one included). */
  plans: number;
  archivedPlans: number;
  hasActivePlan: boolean;
  /** Downloads in the last ~90 days (Analytics Engine retention); null when unavailable. */
  exports: number | null;
  /** Connected assistant names. */
  assistants: string[];
  banned: boolean;
  supportAccessUntil: string | null;
}

export interface AdminRunnerList {
  /** Matching the query + filters. */
  total: number;
  /** Every runner. */
  all: number;
  page: number;
  perPage: number;
  rows: AdminRunnerRow[];
  /** How many runners each filter chip matches on its own. */
  counts: Record<RunnerFilter, number>;
  analytics: AdminAnalytics;
}

export interface AdminEvent {
  at: string;
  event: string;
  runnerId: string | null;
  /** The event's dimensions, named from the catalogue. */
  fields: Record<string, string>;
  durationMs: number | null;
}

export interface AdminAuditEntry {
  id: string;
  at: string;
  adminEmail: string;
  action: string;
  targetUserId: string | null;
  reason: string;
  before: Record<string, unknown> | null;
  after: Record<string, unknown> | null;
}

export interface AdminRunnerDetail {
  runner: {
    id: string;
    name: string;
    email: string;
    emailVerified: boolean;
    emailVerifiedAt: string | null;
    createdAt: string;
    lastSeenAt: string | null;
    banned: boolean;
    banReason: string | null;
    banExpires: string | null;
    /** password | google | apple | microsoft | … */
    methods: string[];
    /** 'admin' accounts can't be banned or deleted from the console. */
    role: string | null;
    /** Sign-in (or reset) attempts for this email are currently refused by the rate limiter. */
    signInLocked: boolean;
  };
  plans: Array<{
    id: string;
    name: string;
    templateId: string | null;
    templateName: string | null;
    methodology: string | null;
    source: "library" | "own" | "assistant";
    weeks: number;
    startAt: string;
    goalRaceAt: string;
    archivedAt: string | null;
    current: boolean;
    createdAt: string;
  }>;
  assistants: Array<{
    clientId: string;
    name: string | null;
    host: string | null;
    access: "read" | "read_write";
    connectedAt: string;
    lastActiveAt: string | null;
  }>;
  sessions: Array<{ createdAt: string; updatedAt: string; expiresAt: string; userAgent: string | null; impersonatedBy: string | null }>;
  supportAccess: {
    active: { grantedAt: string; expiresAt: string; scope: string } | null;
    lastExpiredAt: string | null;
    /** Recent support visits, newest first. */
    sessions: AdminSupportSession[];
  };
  audit: AdminAuditEntry[];
  /** Up to the last 500 events in ~90 days, newest first; null when Analytics Engine is unavailable. */
  activity: AdminEvent[] | null;
  analytics: AdminAnalytics;
}

// ── insight pages ─────────────────────────────────────────────────────

/** Every MCP tool and whether it changes a runner's data. Kept in step with the server's registry by a test. */
export const MCP_TOOL_KINDS: Record<string, "read" | "write"> = {
  list_plans: "read",
  get_plan: "read",
  get_week: "read",
  get_pace_profile: "read",
  list_library_plans: "read",
  get_library_plan: "read",
  suggest_plan: "read",
  get_plan_download: "read",
  whoami: "read",
  get_notation_guide: "read",
  get_adherence: "read",
  get_reflections: "read",
  get_observations: "read",
  create_plan: "write",
  update_plan: "write",
  update_plan_week: "write",
  start_plan: "write",
  start_suggested_plan: "write",
  set_pace_profile: "write",
  set_day: "write",
  swap_days: "write",
  archive_plan: "write",
  restore_plan: "write",
  set_active_plan: "write",
  log_activity: "write",
  confirm_match: "write",
  remove_run: "write",
  undo: "write",
  set_session_outcome: "write",
  log_reflection: "write",
  log_observation: "write",
  propose_plan_change: "write",
  request_plan_change_grant: "write",
  return_ramp: "write",
  recovery: "write",
  dismiss_observation: "write",
  get_data_download: "read",
};

export interface AdminExports {
  range: AdminRange;
  analytics: AdminAnalytics;
  /** The current window's UTC days; byFormatDaily lines up with it. */
  days: string[];
  byFormatDaily: Record<string, number[]> | null;
  total: number | null;
  /** Runners with at least one download. */
  exporters: number | null;
  /** (runner, plan) pairs by how many times the plan was downloaded: 1, 2, 3, 4, 5+. */
  histogram: [number, number, number, number, number] | null;
  /** (runner, plan) pairs, and how many of them were downloaded 2+ times. */
  pairs: number | null;
  repeatPairs: number | null;
  via: { app: number; assistantLink: number } | null;
  multiFormat: Array<{
    runner: { id: string; name: string | null; email: string | null };
    planId: string;
    planName: string | null;
    formats: Record<string, number>;
    downloads: number;
    first: string;
    last: string;
  }> | null;
}

export interface AdminAssistants {
  range: AdminRange;
  analytics: AdminAnalytics;
  days: string[];
  /** Runner ↔ app connections right now (consents). */
  connectedApps: number;
  clients: Array<{
    clientId: string;
    name: string | null;
    host: string | null;
    /** Redirects to localhost — a developer tool, not a public assistant. */
    local: boolean;
    runners: number;
    readWrite: number;
    calls: number | null;
    lastCallAt: string | null;
  }>;
  activeRunners: number | null;
  calls: number | null;
  errors: number | null;
  rateLimited: number | null;
  daily: { calls: number[]; errors: number[] } | null;
  /** Every tool, including ones nobody called. */
  tools: Array<{ tool: string; calls: number; errors: number; avgMs: number }> | null;
  weekly: Array<{ start: string; connects: number; disconnects: number }> | null;
}

export const EVENT_GROUPS = ["all", "auth", "plan", "export", "mcp", "api"] as const;
export type EventGroup = (typeof EVENT_GROUPS)[number];

export interface AdminEventsPage {
  range: AdminRange;
  analytics: AdminAnalytics;
  events: Array<AdminEvent & { runner: { name: string; email: string } | null }> | null;
  /** Counts per group over the whole window (ignoring the search). */
  groups: Record<EventGroup, number> | null;
  /** Pass as `before` to get the next (older) page; null when this is the last. */
  nextBefore: string | null;
}

export interface AdminAuditPage {
  entries: Array<AdminAuditEntry & { runner: { name: string; email: string } | null }>;
}

// ── Support actions (POST runners/:id/actions/:action) ────────────────

export const SUPPORT_ACTIONS = [
  "verify-email",
  "resend-verification",
  "send-password-reset",
  "clear-rate-limit",
  "revoke-sessions",
  "disconnect-app",
  "restore-plan",
  "set-active-plan",
  "ban",
  "unban",
  "delete-account",
] as const;
export type SupportAction = (typeof SUPPORT_ACTIONS)[number];

/** Actions that need the runner's email typed back before they run. */
export const DESTRUCTIVE_ACTIONS: readonly SupportAction[] = ["ban", "delete-account"];

/** A reason shorter than this is refused — it's what the audit log is for. */
export const MIN_REASON_LENGTH = 8;

export interface SupportActionRequest {
  reason: string;
  /** Destructive actions only: the runner's email, typed by the admin. */
  confirmEmail?: string | null;
  /** disconnect-app. */
  clientId?: string | null;
  /** restore-plan, set-active-plan. */
  planId?: string | null;
}

export interface SupportActionResult {
  action: SupportAction;
  audit: AdminAuditEntry;
  /** delete-account: the runner no longer exists. */
  deleted: boolean;
}
